import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { maintenanceAPI } from '../lib/api';
import { toast } from 'sonner';
import { Star, CheckCircle, X } from 'lucide-react';

const SatisfactionSurvey = ({ ticketId, ticketTitle, onComplete }) => {
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [existingSurvey, setExistingSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkExisting();
  }, [ticketId]);

  const checkExisting = async () => {
    try {
      const res = await maintenanceAPI.getSurvey(ticketId);
      if (res.data.survey) {
        setExistingSurvey(res.data.survey);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmitting(true);
    try {
      await maintenanceAPI.submitSurvey(ticketId, {
        rating,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    setDismissed(true);
    onComplete?.();
  };

  if (loading) return null;
  if (existingSurvey) {
    return (
      <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/20 flex items-center gap-3" data-testid={`survey-completed-${ticketId}`}>
        <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
        <div className="flex items-center gap-1">
          <span className="text-sm text-emerald-400">You rated this</span>
          {[1, 2, 3, 4, 5].map(s => (
            <Star key={s} className={`w-3.5 h-3.5 ${s <= existingSurvey.rating ? 'fill-gold text-gold' : 'text-gray-600'}`} />
          ))}
        </div>
      </div>
    );
  }
  if (dismissed) return null;
  if (submitted) {
    return (
      <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/20 flex items-center gap-2" data-testid={`survey-thanks-${ticketId}`}>
        <CheckCircle className="w-4 h-4 text-emerald-400" />
        <span className="text-sm text-emerald-400">Thanks for your feedback!</span>
      </div>
    );
  }

  return (
    <div className="mt-3 p-4 bg-gold/5 border border-gold/20" data-testid={`survey-prompt-${ticketId}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-medium text-gold">How was your experience?</p>
          <p className="text-xs text-muted-foreground mt-0.5">Rate the resolution of "{ticketTitle}"</p>
        </div>
        <button
          onClick={handleSkip}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title="Skip survey"
          data-testid={`survey-skip-${ticketId}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Star Rating */}
      <div className="flex items-center gap-1 mb-3" data-testid={`survey-stars-${ticketId}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            onClick={() => setRating(star)}
            className="transition-transform hover:scale-110"
            data-testid={`survey-star-${star}-${ticketId}`}
          >
            <Star
              className={`w-7 h-7 transition-colors ${
                star <= (hoveredStar || rating)
                  ? 'fill-gold text-gold'
                  : 'text-gray-600 hover:text-gray-400'
              }`}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="text-xs text-muted-foreground ml-2">
            {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
          </span>
        )}
      </div>

      {/* Optional Comment */}
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Any additional feedback? (optional)"
        className="bg-white border-[#e5e7eb] text-sm min-h-[60px] mb-3"
        data-testid={`survey-comment-${ticketId}`}
      />

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          className="bg-gold text-white hover:bg-gold-light"
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          data-testid={`survey-submit-${ticketId}`}
        >
          {submitting ? 'Submitting...' : 'Submit Rating'}
        </Button>
        <button
          onClick={handleSkip}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`survey-skip-link-${ticketId}`}
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default SatisfactionSurvey;
